import {
  Badge,
  Box,
  Button,
  Flex,
  Pagination,
  SimpleGrid,
  Skeleton,
  Table,
  Text,
  createStyles,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconChevronDown,
  IconChevronRight,
  IconClipboardX,
  IconExternalLink,
  IconFileAlert,
  IconListCheck,
  IconPill,
} from "@tabler/icons-react";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import { getSkypointCriticalAlerts } from "../../../services/skypointAlerts";
import { enterProviderView } from "../../../services/skypointProvider";

// Visual + table metadata for each critical-alert category.
const TYPE_META = {
  missingNote: {
    label: "Missing Notes",
    description: "Scheduled progress notes not submitted per charting rules",
    Icon: IconClipboardX,
    iconColor: "#F04438",
    iconBg: "#FEF3F2",
    activeBorder: "#F04438",
    typeColumnLabel: "Note Type",
  },
  missingTask: {
    label: "Missing Tasks",
    description: "ADL / scheduled tasks not completed per charting rules",
    Icon: IconListCheck,
    iconColor: "#F04438",
    iconBg: "#FEF3F2",
    activeBorder: "#F04438",
    typeColumnLabel: "Task Type",
  },
  missingMAR: {
    label: "Missing MAR",
    description: "Scheduled medication doses not administered",
    Icon: IconPill,
    iconColor: "#F04438",
    iconBg: "#FEF3F2",
    activeBorder: "#F04438",
    typeColumnLabel: "MAR Rule",
  },
  expiredDocument: {
    label: "Expired Documents",
    description: "Documents whose expiry date has passed",
    Icon: IconFileAlert,
    iconColor: "#F04438",
    iconBg: "#FEF3F2",
    activeBorder: "#F04438",
    typeColumnLabel: "Document",
  },
};

const useStyles = createStyles((theme) => ({
  container: { width: "100%" },
  stickyBanner: { position: "sticky", top: 0, zIndex: 100 },
  content: {
    padding: "24px 28px",
    [theme.fn.smallerThan("sm")]: { padding: "16px 12px" },
  },
  backRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#667085",
    cursor: "pointer",
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 12,
    "&:hover": { color: "#101828" },
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    marginBottom: 20,
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    background: "#FEF3F2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  title: {
    fontFamily: "Inter",
    fontSize: 22,
    fontWeight: 700,
    color: "#101828",
    lineHeight: 1.2,
  },
  subtitle: {
    fontFamily: "Inter",
    fontSize: 13,
    color: "#667085",
    marginTop: 2,
  },
  card: {
    background: "#FFFFFF",
    border: "1px solid #EAECF0",
    borderRadius: 12,
    padding: 20,
    cursor: "pointer",
    transition: "box-shadow 150ms ease, border-color 150ms ease, transform 100ms ease",
    "&:hover": {
      boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
      transform: "translateY(-1px)",
    },
  },
  cardActive: {
    borderColor: "#F04438",
    borderWidth: 2,
    padding: 19, // compensate for thicker border so layout stays stable
    boxShadow: "0 4px 14px rgba(240, 68, 56, 0.18)",
  },
  cardDisabled: {
    opacity: 0.55,
    cursor: "default",
    "&:hover": { boxShadow: "none", transform: "none" },
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
  },
  cardIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  cardCount: {
    fontFamily: "Inter",
    fontSize: 32,
    fontWeight: 700,
    color: "#101828",
    lineHeight: 1.1,
  },
  cardLabel: {
    fontFamily: "Inter",
    fontSize: 14,
    fontWeight: 600,
    color: "#101828",
    marginTop: 6,
  },
  cardDescription: {
    fontFamily: "Inter",
    fontSize: 12,
    color: "#667085",
    marginTop: 2,
  },
  emptyState: {
    background: "#FFFFFF",
    border: "1px solid #EAECF0",
    borderRadius: 12,
    padding: "48px 20px",
    textAlign: "center",
  },
  emptyTitle: {
    fontFamily: "Inter",
    fontSize: 16,
    fontWeight: 600,
    color: "#101828",
    marginBottom: 4,
  },
  emptyBody: {
    fontFamily: "Inter",
    fontSize: 13,
    color: "#667085",
  },
  tableSection: { marginTop: 24 },
  tableHeading: {
    fontFamily: "Inter",
    fontSize: 16,
    fontWeight: 600,
    color: "#101828",
    marginBottom: 12,
  },
  tableWrap: {
    background: "#FFFFFF",
    border: "1px solid #EAECF0",
    borderRadius: 12,
    overflow: "hidden",
  },
  table: {
    "& thead tr th": {
      backgroundColor: "#F9FAFB",
      fontFamily: "Inter",
      fontSize: 12,
      fontWeight: 600,
      color: "#667085",
      textTransform: "uppercase",
      letterSpacing: 0.4,
      borderBottom: "1px solid #EAECF0",
    },
    "& tbody tr td": {
      fontFamily: "Inter",
      fontSize: 13,
      color: "#101828",
      borderBottom: "1px solid #F2F4F7",
      verticalAlign: "middle",
    },
    "& tbody tr:last-of-type td": { borderBottom: "none" },
  },
  tableEmpty: {
    padding: "48px 20px",
    textAlign: "center",
    color: "#667085",
    fontFamily: "Inter",
    fontSize: 14,
  },
  groupRow: {
    backgroundColor: "#F9FAFB",
    cursor: "pointer",
    userSelect: "none",
    "& td": {
      borderBottom: "1px solid #EAECF0 !important",
      padding: "10px 16px !important",
    },
    "&:hover td": {
      backgroundColor: "#F3F4F6",
    },
  },
  childRow: {
    "& td:first-of-type": {
      paddingLeft: "40px !important",
    },
  },
  paginationRow: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "12px 16px",
    borderTop: "1px solid #EAECF0",
  },
}));

const CRITICAL_TYPES = ["missingNote", "missingTask", "missingMAR", "expiredDocument"];
const LIMIT = 25;

const CriticalAlerts = ({ isSidebarOpen }) => {
  const { classes, cx } = useStyles();
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state?.profile);
  const { dashboardMetrics, dashboardMetricsLoading } = useSelector(
    (state) => state?.dashboard
  );
  const { criticalAlerts } = useSelector((state) => state?.skypointAlerts);

  const snapshot = dashboardMetrics?.complianceSnapshot?.criticalAlerts;
  const activeCategories = useMemo(
    () => snapshot?.activeCategories || [],
    [snapshot]
  );
  const totalItems = snapshot?.totalItems || 0;
  const overviewLoading = dashboardMetricsLoading && !snapshot;

  // Build the count lookup including zero-count categories so the cards
  // render uniformly (active ones are clickable, empty ones are dimmed).
  const countByType = useMemo(() => {
    const m = Object.fromEntries(CRITICAL_TYPES.map((t) => [t, 0]));
    activeCategories.forEach((c) => {
      m[c.key] = c.count;
    });
    return m;
  }, [activeCategories]);

  const firstActive = useMemo(
    () => CRITICAL_TYPES.find((t) => (countByType[t] || 0) > 0),
    [countByType]
  );

  const [selectedType, setSelectedType] = useState(firstActive || null);
  const [page, setPage] = useState(1);
  const [expandedProviders, setExpandedProviders] = useState(new Set());

  // When the snapshot loads, default-select the first active category.
  useEffect(() => {
    if (!selectedType && firstActive) setSelectedType(firstActive);
  }, [firstActive, selectedType]);

  // Fetch the table data whenever the selected type or page changes.
  useEffect(() => {
    if (!selectedType) return;
    getSkypointCriticalAlerts({ page, limit: LIMIT, type: selectedType });
  }, [selectedType, page]);

  // Collapse all groups when the category or page changes.
  useEffect(() => {
    setExpandedProviders(new Set());
  }, [selectedType, page]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((criticalAlerts.total || 0) / LIMIT)),
    [criticalAlerts.total]
  );

  const handleSelectCard = (type) => {
    if ((countByType[type] || 0) === 0) return;
    if (type === selectedType) return;
    setPage(1);
    setSelectedType(type);
  };

  const handleViewProvider = async (providerId, providerName) => {
    const ok = await enterProviderView(providerId, providerName);
    if (ok) navigate("/home/provider-view");
  };

  const toggleProvider = (key) => {
    setExpandedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const items = useMemo(
    () => (criticalAlerts.items || []).filter((i) => i.type === selectedType),
    [criticalAlerts.items, selectedType]
  );

  const groups = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const key = item.providerId || item.providerName;
      if (!map.has(key)) {
        map.set(key, {
          key,
          providerId: item.providerId,
          providerName: item.providerName,
          items: [],
        });
      }
      map.get(key).items.push(item);
    });
    return Array.from(map.values());
  }, [items]);

  const selectedMeta = selectedType ? TYPE_META[selectedType] : null;
  const isExpiredDoc = selectedType === "expiredDocument";
  const totalCols = isExpiredDoc ? 5 : 4;

  return (
    <Box className={classes.container}>
      <Box className={classes.stickyBanner}>
        <AppNavbar
          title="Home"
          profile={profile}
          isSidebarOpen={isSidebarOpen}
        />
      </Box>

      <Box className={classes.content}>
        <Box
          className={classes.backRow}
          onClick={() => navigate("/home/dashboard-skypoint")}
        >
          <IconArrowLeft size={16} />
          <span>Back to dashboard</span>
        </Box>

        <Box className={classes.headerRow}>
          <Box className={classes.headerIcon}>
            <IconAlertTriangle size={22} color="#F04438" />
          </Box>
          <Box>
            <Text className={classes.title}>Critical Alerts</Text>
            <Text className={classes.subtitle}>
              {overviewLoading
                ? "Loading…"
                : activeCategories.length === 0
                  ? "No active critical categories. All clear."
                  : `${activeCategories.length} active ${
                      activeCategories.length === 1 ? "category" : "categories"
                    } · ${totalItems} ${totalItems === 1 ? "item" : "items"} total`}
            </Text>
          </Box>
        </Box>

        {overviewLoading ? (
          <SimpleGrid cols={4} breakpoints={[{ maxWidth: 1100, cols: 2 }, { maxWidth: 700, cols: 1 }]}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={180} radius={12} />
            ))}
          </SimpleGrid>
        ) : activeCategories.length === 0 ? (
          <Box className={classes.emptyState}>
            <Text className={classes.emptyTitle}>All clear</Text>
            <Text className={classes.emptyBody}>
              No charting-rule violations or expired documents across your
              providers.
            </Text>
          </Box>
        ) : (
          <SimpleGrid cols={4} breakpoints={[{ maxWidth: 1100, cols: 2 }, { maxWidth: 700, cols: 1 }]}>
            {CRITICAL_TYPES.map((type) => {
              const meta = TYPE_META[type];
              const count = countByType[type] || 0;
              const isEmpty = count === 0;
              const isActive = type === selectedType;
              const Icon = meta.Icon;
              return (
                <Box
                  key={type}
                  className={cx(
                    classes.card,
                    isActive && classes.cardActive,
                    isEmpty && classes.cardDisabled
                  )}
                  onClick={() => handleSelectCard(type)}
                >
                  <Box className={classes.cardHeader}>
                    <Box
                      className={classes.cardIcon}
                      sx={{ backgroundColor: meta.iconBg }}
                    >
                      <Icon size={22} color={meta.iconColor} />
                    </Box>
                    <Text className={classes.cardCount}>{count}</Text>
                  </Box>
                  <Text className={classes.cardLabel}>{meta.label}</Text>
                  <Text className={classes.cardDescription}>
                    {meta.description}
                  </Text>
                </Box>
              );
            })}
          </SimpleGrid>
        )}

        {selectedType && selectedMeta && activeCategories.length > 0 && (
          <Box className={classes.tableSection}>
            <Text className={classes.tableHeading}>
              {selectedMeta.label}
              {criticalAlerts.total > 0 &&
                ` · ${criticalAlerts.total} ${
                  criticalAlerts.total === 1 ? "item" : "items"
                }`}
            </Text>
            <Box className={classes.tableWrap}>
              <Table className={classes.table} verticalSpacing="md">
                <thead>
                  {isExpiredDoc ? (
                    <tr>
                      <th style={{ width: 110 }}>User Type</th>
                      <th style={{ width: 220 }}>Name</th>
                      <th>{selectedMeta.typeColumnLabel}</th>
                      <th style={{ width: 130 }}>Date</th>
                      <th style={{ width: 160 }}></th>
                    </tr>
                  ) : (
                    <tr>
                      <th style={{ width: 220 }}>Client Name</th>
                      <th>{selectedMeta.typeColumnLabel}</th>
                      <th style={{ width: 130 }}>Date</th>
                      <th style={{ width: 160 }}></th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {criticalAlerts.loading ? (
                    [...Array(5)].map((_, i) => (
                      <tr key={i}>
                        <td colSpan={totalCols}>
                          <Skeleton height={32} radius={6} />
                        </td>
                      </tr>
                    ))
                  ) : groups.length === 0 ? (
                    <tr>
                      <td colSpan={totalCols} className={classes.tableEmpty}>
                        No items in this category.
                      </td>
                    </tr>
                  ) : (
                    groups.map((group) => {
                      const isExpanded = expandedProviders.has(group.key);
                      return (
                        <React.Fragment key={group.key}>
                          <tr
                            className={classes.groupRow}
                            onClick={() => toggleProvider(group.key)}
                          >
                            <td colSpan={totalCols}>
                              <Flex align="center" gap={10}>
                                {isExpanded ? (
                                  <IconChevronDown size={16} color="#667085" />
                                ) : (
                                  <IconChevronRight size={16} color="#667085" />
                                )}
                                <Text weight={600} size="sm" color="#101828">
                                  {group.providerName || "Unknown Provider"}
                                </Text>
                                <Badge
                                  size="sm"
                                  variant="filled"
                                  color="red"
                                  radius="xl"
                                >
                                  {group.items.length}{" "}
                                  {group.items.length === 1 ? "item" : "items"}
                                </Badge>
                              </Flex>
                            </td>
                          </tr>
                          {isExpanded &&
                            group.items.map((item) => (
                              <tr
                                key={`${item.type}-${item.relatedId}`}
                                className={classes.childRow}
                              >
                                {isExpiredDoc && (
                                  <td>
                                    <Badge
                                      variant="light"
                                      size="sm"
                                      color={
                                        item.userType === "Client"
                                          ? "blue"
                                          : item.userType === "Staff"
                                            ? "violet"
                                            : item.userType === "Facility"
                                              ? "teal"
                                              : "gray"
                                      }
                                    >
                                      {item.userType || "—"}
                                    </Badge>
                                  </td>
                                )}
                                <td>
                                  <Text
                                    size="sm"
                                    color={
                                      (isExpiredDoc ? item.ownerName : item.clientName)
                                        ? "dark"
                                        : "dimmed"
                                    }
                                  >
                                    {(isExpiredDoc ? item.ownerName : item.clientName) || "—"}
                                  </Text>
                                </td>
                                <td>
                                  <Text size="sm">{item.ruleName || item.title}</Text>
                                  {item.subtitle && (
                                    <Text size="xs" color="dimmed" mt={2}>
                                      {item.subtitle}
                                    </Text>
                                  )}
                                </td>
                                <td>
                                  <Text size="xs" color="dimmed">
                                    {item.relatedDate
                                      ? new Date(item.relatedDate).toLocaleDateString()
                                      : "—"}
                                  </Text>
                                </td>
                                <td>
                                  <Button
                                    size="xs"
                                    variant="subtle"
                                    leftIcon={<IconExternalLink size={14} />}
                                    onClick={() =>
                                      handleViewProvider(item.providerId, item.providerName)
                                    }
                                  >
                                    Ogiso Core
                                  </Button>
                                </td>
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </Table>
              {totalPages > 1 && (
                <Box className={classes.paginationRow}>
                  <Pagination
                    total={totalPages}
                    value={page}
                    onChange={setPage}
                    size="sm"
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CriticalAlerts;
